import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Header } from "@/components/header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PinInput } from "@/components/landing/pin-input";
import { Badge } from "@/components/ui/badge";
import { joinSession, getPlayBootstrap } from "@/lib/student.functions";
import { useErrorReporter } from "@/components/error-reporter";
import { toAppError } from "@/lib/errors";
import { useServerFn } from "@tanstack/react-start";
import { AlertCircle, Gamepad2, Loader2 } from "lucide-react";

export const Route = createFileRoute("/join/$pin")({
  component: JoinPage,
});

function JoinPage() {
  const { pin } = Route.useParams();
  const [nickname, setNickname] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [pinError, setPinError] = useState(null);
  const join = useServerFn(joinSession);
  const bootstrap = useServerFn(getPlayBootstrap);
  const navigate = useNavigate();
  const { reportError } = useErrorReporter();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stored = localStorage.getItem(`subiza:pin:${pin}`);
        if (stored) {
          navigate({ to: "/play/$pin", params: { pin }, replace: true });
          return;
        }
        const res = await bootstrap({ data: { pin } });
        if (cancelled) return;
        if (res.session.status === "ended") {
          setPinError("This game has already ended. Ask your teacher for a new PIN.");
        }
      } catch (err) {
        // Rendered inline in the card rather than as a toast, because this is a
        // pre-check the student is looking straight at. Normalized through the
        // catalog so a wrong PIN reads as "That game code isn't right" instead
        // of surfacing whatever the transport happened to say. Deliberately not
        // reported to Sentry: a mistyped PIN is an expected outcome, not a fault.
        if (!cancelled) {
          const appError = toAppError(err, { area: "student.bootstrap" });
          setPinError(
            appError.code === "INVALID_PIN"
              ? "That game code isn't right. Check the code your teacher gave you."
              : appError.userMessage,
          );
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pin, navigate]);

  async function handleJoin(e) {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await join({ data: { pin, nickname: nickname.trim() } });
      const payload = JSON.stringify(res);
      localStorage.setItem(`subiza:${res.sessionId}`, payload);
      localStorage.setItem(`subiza:pin:${pin}`, payload);
      navigate({ to: "/play/$pin", params: { pin }, replace: true });
    } catch (err) {
      // Goes through the reporter so the friendly catalog copy is shown, the
      // code is tagged in Sentry, and a superadmin gets the technical panel.
      // No `retry` handler: this form stays mounted with the student's typed
      // nickname, so submitting again is already the obvious next step.
      reportError(err, { area: "student.join" });
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-hero">
      <Header />
      <div className="container mx-auto flex justify-center px-4 py-16">
        <Card className="glass relative w-full max-w-md overflow-hidden rounded-3xl border-2 p-0 shadow-elegant">
          <div aria-hidden="true" className="absolute inset-x-0 top-0 h-1 bg-gradient-primary" />
          <div className="border-b border-border/60 bg-gradient-card px-4 pb-5 pt-6 text-center sm:px-8 sm:pb-7 sm:pt-9">
            <Badge variant="secondary" className="mb-4 gap-1.5 rounded-full px-3 py-1">
              <Gamepad2 className="size-3.5" aria-hidden="true" /> Game PIN
            </Badge>
            <PinInput value={pin} readOnly label="Game PIN" />
          </div>

          <div className="p-5 sm:p-8">
            {checking ? (
              <div className="flex items-center justify-center gap-2 py-2 text-sm text-muted">
                <Loader2 className="size-4 animate-spin" aria-hidden="true" /> Checking PIN…
              </div>
            ) : pinError ? (
              <div className="space-y-4 text-center">
                <span className="mx-auto grid size-11 place-items-center rounded-full bg-destructive/10 text-destructive">
                  <AlertCircle className="size-5" aria-hidden="true" />
                </span>
                <p role="alert" className="font-medium text-destructive">
                  {pinError}
                </p>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/">Back to home</Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={handleJoin} className="space-y-4">
                <div>
                  <div className="flex items-baseline justify-between">
                    <label htmlFor="nickname" className="text-sm font-medium">
                      Pick a nickname
                    </label>
                    <span className="text-xs tabular-nums text-muted">{nickname.length}/20</span>
                  </div>
                  <Input
                    id="nickname"
                    autoFocus
                    maxLength={20}
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    placeholder="e.g. SuperLearner42"
                    autoComplete="off"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    className="mt-1.5 text-lg"
                    required
                  />
                  <p className="mt-1.5 text-xs text-muted">
                    This is how classmates will see you on the leaderboard.
                  </p>
                </div>
                <Button
                  type="submit"
                  size="lg"
                  disabled={loading || !nickname.trim()}
                  className="w-full bg-gradient-primary text-base shadow-elegant transition-transform hover:-translate-y-0.5 active:translate-y-0"
                >
                  {loading ? (
                    <>
                      <Loader2 className="size-4 animate-spin" aria-hidden="true" /> Joining…
                    </>
                  ) : (
                    "Enter the lobby"
                  )}
                </Button>

                <p className="text-center text-xs text-muted">
                  Wrong game?{" "}
                  <Link to="/" hash="join" className="font-medium text-accent hover:underline">
                    Try another PIN
                  </Link>
                </p>
              </form>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
